/**
 * Thin fetch wrapper for the rd-mcp REST API. All admin / portal calls
 * funnel through here so we have one place to attach the Bearer token,
 * map errors to a typed shape, and surface 401 → log the user out.
 *
 * We DO NOT touch /mcp/ from the SPA - that's Claude's surface only.
 */

const BASE = (import.meta.env.VITE_API_BASE as string) ?? "";

export type ApiError = {
  status: number;
  message: string;
  detail?: unknown;
};

let unauthorizedHandler: (() => void) | null = null;

export function onUnauthorized(handler: () => void) {
  unauthorizedHandler = handler;
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit & { token?: string | null } = {},
): Promise<T> {
  const { token, headers, ...rest } = init;
  const finalHeaders = new Headers(headers ?? {});
  if (token) finalHeaders.set("Authorization", `Bearer ${token}`);
  if (rest.body && !finalHeaders.has("Content-Type")) {
    finalHeaders.set("Content-Type", "application/json");
  }
  const res = await fetch(`${BASE}${path}`, { ...rest, headers: finalHeaders });

  if (res.status === 204) return undefined as T;

  let body: unknown = undefined;
  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("application/json")) {
    body = await res.json().catch(() => undefined);
  } else {
    body = await res.text().catch(() => undefined);
  }

  if (!res.ok) {
    if (res.status === 401 && unauthorizedHandler) unauthorizedHandler();
    const message =
      (typeof body === "object" && body && "detail" in body
        ? String((body as { detail: unknown }).detail)
        : undefined) ?? `${res.status} ${res.statusText}`;
    const err: ApiError = { status: res.status, message, detail: body };
    throw err;
  }
  return body as T;
}

// ---------- typed endpoints ----------

export type LoginResponse = { access_token: string; token_type: string };
export type Me = { id: number; username: string; is_admin: boolean; disabled: boolean };
export type Grant = { rds_id: number; schema_name: string };
export type RdsRegistration = {
  id: number;
  display_name: string;
  host: string;
  port: number;
  secret_arn: string;
  schemas: string[];
};

export type AddRdsResponse = { id: number; schemas: string[]; created: boolean };
export type User = Me;

export const api = {
  login: (username: string, password: string) =>
    apiFetch<LoginResponse>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),
  refresh: (token: string) =>
    apiFetch<LoginResponse>("/api/auth/refresh", { method: "POST", token }),
  me: (token: string) => apiFetch<Me>("/api/me", { token }),
  myGrants: (token: string) => apiFetch<Grant[]>("/api/me/grants", { token }),

  // Admin
  listUsers: (token: string) => apiFetch<User[]>("/api/admin/users", { token }),
  createUser: (token: string, body: { username: string; password: string; is_admin: boolean }) =>
    apiFetch<User>("/api/admin/users", { method: "POST", token, body: JSON.stringify(body) }),
  patchUser: (
    token: string,
    id: number,
    body: Partial<{ is_admin: boolean; disabled: boolean; username: string }>,
  ) => apiFetch<User>(`/api/admin/users/${id}`, { method: "PATCH", token, body: JSON.stringify(body) }),
  deleteUser: (token: string, id: number) =>
    apiFetch<{ deleted: boolean; id: number; username: string }>(`/api/admin/users/${id}`, {
      method: "DELETE",
      token,
    }),
  setPassword: (token: string, id: number, new_password: string) =>
    apiFetch<{ ok: boolean }>(`/api/admin/users/${id}/password`, {
      method: "POST",
      token,
      body: JSON.stringify({ new_password }),
    }),

  listRds: (token: string) => apiFetch<RdsRegistration[]>("/api/admin/rds", { token }),
  createRds: (
    token: string,
    body: { display_name: string; host: string; port: number; secret_arn: string },
  ) =>
    apiFetch<AddRdsResponse>("/api/admin/rds", {
      method: "POST",
      token,
      body: JSON.stringify(body),
    }),
  refreshRdsSchemas: (token: string, id: number) =>
    apiFetch<{ schemas: string[] }>(`/api/admin/rds/${id}/refresh-schemas`, {
      method: "POST",
      token,
    }),
  deleteRds: (token: string, id: number) =>
    apiFetch<{ deleted: boolean; id: number; display_name: string }>(`/api/admin/rds/${id}`, {
      method: "DELETE",
      token,
    }),

  getUserGrants: (token: string, userId: number) =>
    apiFetch<Grant[]>(`/api/admin/users/${userId}/grants`, { token }),
  putUserGrants: (token: string, userId: number, grants: Grant[]) =>
    apiFetch<{ ok: boolean }>(`/api/admin/users/${userId}/grants`, {
      method: "PUT",
      token,
      body: JSON.stringify({ grants }),
    }),
};
